<template>
  <div>
    <PageHeader :title="t('title')" />

    <SearchToolbar
      class="mb-6"
      :placeholder="t('search_placeholder')"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      :has-active-period="dateRange.some(Boolean)"
      :initial-keywords="keywords"
      @update-filter-search="updateFilterSearch"
      @update-keyword-search="updateKeywordSearch"
      @clear-filters="dateRangeUpdated([])"
    >
      <DateRangePicker :placeholder="t('period')" v-model="dateRange" @update:modelValue="dateRangeUpdated" />
    </SearchToolbar>

    <Loading v-if="loading" />
    <div v-else-if="planSales.length === 0" class="text-center mt-4 py-2">
      <p>{{ t("no_results") }}</p>
    </div>
    <PlanSaleTable
      v-else
      :plan-sales="planSales"
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-count="totalCount"
      @update-page="retrievePlanSales"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { DateRangePicker, Loading, PageHeader, SearchToolbar } from "@estaly/ui";
import PlanSaleTable from "@/components/plan_sales/PlanSaleTable.vue";

const INDEX_INCLUDES = ["customer", "plan_sale_product", "store", "cancellation"];

export default {
  name: "PlanSaleList",

  components: { DateRangePicker, Loading, PageHeader, SearchToolbar, PlanSaleTable },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: true,
      availableFilters: { status: [], stores: [] },
      selectedFilters: { status: [], stores: [] },
      dateRange: [],
      keywords: null,
    };
  },

  computed: {
    ...mapState("planSales", {
      planSales: "index",
      currentPage: "currentPage",
      totalPages: "totalPages",
      totalCount: "totalCount",
    }),
    ...mapState("stores", { stores: "index" }),
    ...mapGetters("planSales", ["statuses"]),

    planSaleParams() {
      return {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        keywords: this.keywords,
        storeIds: this.selectedFilters.stores,
        status: this.selectedFilters.status,
      };
    },
  },

  async created() {
    this.availableFilters.status = this.statuses();

    await this.fetchStores({ perPage: 2000 });
    this.availableFilters.stores = this.stores.map((store) => ({ label: store.salesChannel, value: store.id }));

    await this.retrievePlanSales();
  },

  methods: {
    ...mapActions("planSales", { fetchPlanSales: "fetchIndex" }),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("notifications", ["notify"]),

    async retrievePlanSales(page = 1) {
      this.loading = true;
      try {
        const data = await this.fetchPlanSales({ ...this.planSaleParams, page, includes: INDEX_INCLUDES });
        if (!data) await this.notify({ category: "simple", type: "error", text: this.t("fetch_error") });
      } finally {
        this.loading = false;
      }
    },

    dateRangeUpdated(dateRange) {
      this.dateRange = dateRange;
      this.retrievePlanSales();
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.retrievePlanSales();
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.retrievePlanSales();
    },
  },
};
</script>

<i18n src="./PlanSaleList.i18n.json"></i18n>
